<template>
  <div>
    <div class="top-bar flex justify-end mb-4">
      <v-button @click="openMergeDuplicatesModal" icon="object-group" icon-pack="fas" color="yellow">
        {{ $t('warehouse_material_merge_duplicates') }}
      </v-button>
      <v-button @click="openAddModal" icon="plus" class="ml-4">
        {{ $t('add_unique_material') }}
      </v-button>
    </div>

    <b-table
      :data="rows"
      :loading="isLoading"
      paginated
      backend-pagination
      :per-page="perPage"
      :total="total"
      :current-page="page"
      @page-change="onPageChange"
      striped
      bordered
      mobile-cards
      icon-pack="fas"
    >
      <b-table-column field="code" :label="$t('unique_material_id')" sortable v-slot="props">
        {{ props.row.code }}
      </b-table-column>

      <b-table-column field="description" :label="$t('description')" sortable v-slot="props">
        {{ props.row.description }}
      </b-table-column>

      <b-table-column field="unit" :label="$t('unit')" sortable v-slot="props">
        {{ props.row.unit && props.row.unit.code ? props.row.unit.code : '-' }}
      </b-table-column>

      <b-table-column field="category" :label="$t('category')" v-slot="props">
        {{ props.row.category || '-' }}
      </b-table-column>

      <b-table-column field="order_number" :label="$t('order_number')" v-slot="props">
        {{ props.row.order_number || '-' }}
      </b-table-column>

      <b-table-column field="provider" :label="$t('provider')" v-slot="props">
        {{ providerName(props.row) }}
      </b-table-column>

      <b-table-column field="drawing_number" :label="$t('drawing_number')" v-slot="props">
        {{ props.row.drawing_number || '-' }}
      </b-table-column>

      <b-table-column field="tag_kks" :label="$t('tag_kks')" v-slot="props">
        {{ props.row.tag_kks || '-' }}
      </b-table-column>

      <b-table-column field="activity" :label="$t('activity')" v-slot="props">
        {{ activityLabel(props.row) }}
      </b-table-column>

      <b-table-column field="actions" :label="$t('actions')" centered width="120" v-slot="props">
        <div class="w-full h-full flex items-center justify-center gap-3">
          <b-tooltip :label="$t('edit')" position="is-top">
            <div @click="openEditModal(props.row)" class="cursor-pointer flex text-blue">
              <b-icon pack="fas" icon="pencil-alt" />
            </div>
          </b-tooltip>
          <b-tooltip
            v-if="Number(props.row.elements_associated || 0) === 0"
            :label="$t('delete')"
            type="is-danger"
            position="is-top"
          >
            <div @click="confirmDeleteMaterial(props.row)" class="cursor-pointer flex text-red">
              <b-icon pack="fas" icon="trash-alt" />
            </div>
          </b-tooltip>
        </div>
      </b-table-column>

      <template #empty>
        <div class="has-text-centered">{{ $t('no_results') }}</div>
      </template>
    </b-table>

    <WarehouseAddMaterialModal
      ref="addMaterialModal"
      :project-id="projectId"
      @saved="loadMaterials"
    />

    <WarehouseMergeDuplicatesModal
      ref="mergeDuplicatesModal"
      :project-id="projectId"
      @merged="loadMaterials"
    />
  </div>
</template>

<script>
import axios from 'axios'
import WarehouseAddMaterialModal from './warehouse-add-material-modal.vue'
import WarehouseMergeDuplicatesModal from './warehouse-merge-duplicates-modal.vue'

export default {
  name: 'warehouse-materials',
  components: {
    WarehouseAddMaterialModal,
    WarehouseMergeDuplicatesModal,
  },
  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
  },
  data: () => ({
    rows: [],
    perPage: 15,
    page: 1,
    total: 0,
    isLoading: false,
  }),
  watch: {
    projectId: {
      immediate: true,
      async handler() {
        this.page = 1
        await this.loadMaterials()
      },
    },
  },
  methods: {
    providerName(row) {
      if (row.provider && row.provider.name) {
        return row.provider.name
      }
      return '-'
    },
    activityLabel(row) {
      const activity = row.activity
      if (!activity) {
        return '-'
      }
      const code = activity.planning_code ? `${activity.planning_code} - ` : ''
      return code + (activity.name || `#${activity.id}`)
    },
    onPageChange(newPage) {
      this.page = newPage
      this.loadMaterials()
    },

    openAddModal() {
      if (this.$refs.addMaterialModal && this.$refs.addMaterialModal.open) {
        this.$refs.addMaterialModal.open()
      }
    },

    openEditModal(material) {
      if (this.$refs.addMaterialModal && this.$refs.addMaterialModal.openEdit) {
        this.$refs.addMaterialModal.openEdit(material)
      }
    },

    openMergeDuplicatesModal() {
      if (this.$refs.mergeDuplicatesModal && this.$refs.mergeDuplicatesModal.open) {
        this.$refs.mergeDuplicatesModal.open()
      }
    },

    async loadMaterials() {
      if (!this.projectId) {
        this.rows = []
        return
      }

      try {
        this.isLoading = true
        const { data } = await axios.get(`/api/v2/project/${this.projectId}/warehouse/materials`, {
          params: {
            page: this.page,
            per_page: this.perPage,
            with_inventory_totals: 1,
          },
        })
        if (data && data.success) {
          // with_inventory_totals añade una fila resumen sin id para líneas sin material: no aplica aquí
          this.rows = (data.materials || []).filter((m) => m && m.id != null)
          this.total = typeof data.total === 'number' ? data.total : 0
          if (typeof data.perPage === 'number') {
            this.perPage = data.perPage
          }
          if (typeof data.currentPage === 'number') {
            this.page = data.currentPage
          }
        } else {
          this.rows = []
          this.total = 0
        }
      } catch (error) {
        this.rows = []
        this.$notify.error('error_loading_data')
      } finally {
        this.isLoading = false
      }
    },
    confirmDeleteMaterial(material) {
      this.$dialog.confirm({
        title: this.$t('delete'),
        message: this.$t('are_you_sure'),
        confirmText: this.$t('delete'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          await this.deleteMaterial(material.id)
        },
      })
    },

    async deleteMaterial(materialId) {
      try {
        this.isLoading = true
        await axios.delete(`/api/v2/project/${this.projectId}/warehouse/materials/${materialId}`)
        this.$notify.success('removed_successfully')
        if (this.rows.length === 1 && this.page > 1) {
          this.page -= 1
        }
        await this.loadMaterials()
      } catch (error) {
        this.$notify.error('save_error')
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>